/**
 * L'onglet Messages de la fiche : la conversation du tuteur avec un élève (route `messenger` côté
 * tuteur, `ChatScreen.js` + `CommonChat.js`, hors live). Décision 10 du lot 1 : la messagerie passe
 * avec Mes élèves, et elle est un onglet de la fiche.
 *
 * Sur le socle de la conversation de l'élève, sans le dupliquer : ses lectures
 * (`useStudentTutorConversationData` — lire le fil, le marquer lu après la lecture et à la sortie,
 * relire au retour au premier plan, retirer les notifications « messages »), ses gestes
 * (`useStudentTutorConversationState` — texte, photo, galerie, document, « Renvoyer », « Copier »,
 * liens), son lecteur et sa file des vocaux. Ce que la branche tuteur a de plus passe par les options
 * de ce socle :
 *  - le fil se trouve par les séances du tuteur, et ses messages sont ceux du compte du tuteur
 *    (`tutorStudentConversationViewModel`) ;
 *  - hors live, l'élève est relu (`getStudent`) à chaque ouverture, comme `CommonChat` le faisait — la
 *    première, quand la fiche s'ouvre sur Messages, par la fiche elle-même : l'onglet ne la double pas ;
 *  - un fichier choisi ne part pas aussitôt : « Nouveau fichier » (`<TutorMediaPreviewModal>`) propose
 *    « Créer une activité » ou « Envoyer dans le chat » ;
 *  - toucher une photo ou un PDF de l'élève ouvre l'activité qui en est née (`onOpenActivity`), sinon
 *    son aperçu, dont « Créer une activité » ouvre la génération sur ce fichier, rattachée au message ;
 *  - une photo envoyée et un vocal sont signés du nom du compte qui écrit, le tuteur (comme le legacy).
 *
 * Rien n'est lu ni marqué lu tant que l'onglet n'est pas celui qu'on regarde dans une fiche visible.
 */
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { API_URL } from "../../../../../../data/constants/apiConstants"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { studentsService } from "../../../../../../data/services/studentsService"
import { ownNetworkFailures } from "../../../../../../data/utility/networkFailureOwner"
import { type TutorConversationPageProps } from "../../../../../../design-system/pages/TutorConversationPage"
import { useVoiceOutbox, type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { IS_WEB } from "../../../../../../engine/shared/platform"
import {
    generationMimeType,
    type TutorChatMedia,
} from "../../../../adapters/tutor/Students/tutorStudentConversationAdapter"
import { selectAccount } from "../../../../view-models/shared/displayedStudent"
import {
    INACTIVE_TUTOR_CONVERSATION,
    makeTutorStudentConversationViewModel,
    selectTutorStudentThreadId,
} from "../../../../view-models/tutor/Students/tutorStudentConversationViewModel"
import { type UITutorStudent } from "../../../types/tutorEleves"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { mergeOutboxes, voiceOutboxEntries } from "../../student/Messaging/conversationVoiceOutbox"
import { messengerVoiceFileName, messengerVoiceOutbox } from "../../student/Messaging/messengerVoiceOutbox"
import { useConversationVoicePlayer } from "../../student/Messaging/useConversationVoicePlayer"
import { useStudentTutorConversationData } from "../../student/Messaging/useStudentTutorConversationData"
import {
    CONVERSATION_ATTACH_SOURCES,
    useStudentTutorConversationState,
    type HeldConversationFile,
} from "../../student/Messaging/useStudentTutorConversationState"
import { TutorStudentMessagesOverlay } from "./TutorStudentMessagesOverlay"
import { initialTutorMediaPreviewState, tutorMediaPreviewReducer } from "./useTutorMediaPreviewState"
import { type TutorGenerateFile, type TutorStudentTab, type TutorStudentTabArgs } from "./tutorStudentTabs"

export type UseTutorStudentMessagesTabArgs = TutorStudentTabArgs & {
    student: UITutorStudent | null
    /**
     * Ouvre la fiche d'une activité de l'élève (l'onglet Activités la porte). Absent : une pièce dont
     * une activité est née s'ouvre dans son aperçu.
     */
    onOpenActivity?: (activityId: string) => void
}

/**
 * Chaque geste de l'onglet et ce qu'il envoie, comme la branche tuteur de `CommonChat.js` (hors live) :
 * la table d'équivalence, tenue par un test par ligne (`useTutorStudentMessagesTab.equivalence.hook.test.tsx`).
 */
export const TUTOR_STUDENT_MESSAGES_EQUIVALENCE = {
    open: {
        gesture:
            "Ouvrir l'onglet Messages, hors live : le fil, puis « lu » après sa lecture ; l'élève relu à chaque retour sur l'onglet (à l'ouverture de la fiche, par la fiche)",
        legacy: "common/messenger/CommonChat.js:226-243, 254-265",
        calls: [
            "getMessages { threadId }",
            "getMarkRead { threadId } (après la lecture)",
            "getStudent { id: élève } (au retour)",
        ],
    },
    in_live: {
        gesture: "Ouvrir l'onglet en live : le fil, sans relire l'élève",
        legacy: "common/messenger/CommonChat.js:232-239 (`!isLiveModeEnabled`)",
        calls: ["getMessages { threadId }"],
    },
    hidden: {
        gesture: "La fiche ouverte sur un autre onglet : rien n'est lu ni marqué lu",
        legacy: "— (l'ancienne messagerie était un écran poussé : fermé, elle ne lisait rien)",
        calls: [],
    },
    send_text: {
        gesture: "Envoyer un texte",
        legacy: "common/messenger/CommonChat.js:202-225",
        calls: ['postMessages { threadId } { message, temporary_id: "message" }'],
    },
    new_photo: {
        gesture: "Choisir une photo : « Nouveau fichier » s'ouvre, rien ne part",
        legacy: "common/messenger/CommonChat.js:724-733, 773-785",
        calls: [],
    },
    send_to_chat: {
        gesture: "« Envoyer dans le chat » depuis « Nouveau fichier » : la photo signée du tuteur",
        legacy: "common/messenger/CommonChat.js:1126-1190",
        calls: ['postImage { threadId } (temporary_id: "test", image: Masteur_Image_<tuteur>_<date>.png)'],
    },
    new_pdf: {
        gesture: "Choisir un PDF, puis « Envoyer dans le chat »",
        legacy: "common/messenger/CommonChat.js:482-510, 1126-1190",
        calls: ['postDocument { threadId } (temporary_id: "test", document: <nom>_<date>.pdf)'],
    },
    open_activity: {
        gesture: "Toucher une pièce dont une activité est née : sa fiche, sans aller-retour",
        legacy: "common/messenger/CommonChat.js:1206-1240 (`todo_activities.concat(activities)`, `TodoModal`)",
        calls: [],
    },
    preview_create: {
        gesture: "Toucher une pièce sans activité : « Aperçu », puis « Créer une activité » sur ce fichier",
        legacy: "common/messenger/CommonChat.js:1108-1124, 1241-1258",
        calls: ["la génération, ouverte sur le fichier rattaché à son message (`message_id`)"],
    },
    leave: {
        gesture: "Quitter l'onglet : le fil marqué lu",
        legacy: "common/messenger/CommonChat.js:283-292",
        calls: ["getMarkRead { threadId }"],
    },
} as const

/**
 * Ce que la génération reçoit de l'aperçu (`handleCreateActivityFromPreview`) : le fichier du legacy
 * (`initialImage`), rattaché à son message s'il en vient.
 */
export function generationFileOf(media: TutorChatMedia): TutorGenerateFile {
    return {
        uri: media.uri,
        name: media.name ?? `file_${Date.now()}`,
        mimeType: generationMimeType(media),
        ...(media.messageId != null ? { messageId: media.messageId } : {}),
    }
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export function useTutorStudentMessagesTab(
    args: UseTutorStudentMessagesTabArgs
): TutorStudentTab<TutorConversationPageProps> {
    const { studentId, visible, active, openGeneration, student, onOpenActivity } = args
    const dispatch = useDispatch()
    const on = visible && active && studentId != null

    const inLive = useSelector((state: ReduxState) => !!state?.liveMode?.isLiveModeEnabled)
    const tutorName = useSelector((state: ReduxState) =>
        on ? ((selectAccount(state)?.display_name as string | undefined) ?? null) : null
    )

    // Le fil d'abord (un id : la lecture ne redessine que s'il change), nul hors de l'onglet ; la boîte
    // d'envoi vit dans les gestes, qui ont besoin du fil.
    const threadId = useSelector((state: ReduxState) => (on ? selectTutorStudentThreadId(state, studentId) : null))

    // À l'écran, la conversation dit elle-même ses échecs réseau : pas de boîte legacy par-dessus.
    useEffect(() => (on ? ownNetworkFailures() : undefined), [on])

    // L'élève relu à chaque ouverture, hors live (`CommonChat.js`, branche tuteur) : ses activités disent
    // ce qui est né d'une pièce. La fiche le lit déjà quand elle s'ouvre, ou change d'élève : l'onglet ne
    // relit qu'en revenant sur lui pour le même élève.
    const shownRef = useRef<{ on: boolean; studentId: unknown } | null>(null)
    useEffect(() => {
        const before = shownRef.current
        shownRef.current = { on, studentId }
        const back = before != null && !before.on && before.studentId === studentId
        if (!on || !back || inLive || studentId == null) return
        dispatch(apiRequest(studentsService.getStudent, { id: studentId }) as never)
    }, [dispatch, on, inLive, studentId])

    const [preview, dispatchPreview] = useReducer(tutorMediaPreviewReducer, initialTutorMediaPreviewState)

    const holdNewFile = useCallback(
        (file: HeldConversationFile) => dispatchPreview({ type: "OPEN_FRESH", media: file }),
        []
    )

    const data = useStudentTutorConversationData(threadId)

    // L'activité née d'une pièce, lue au moment du geste.
    const activityIdsRef = useRef<Map<string, string>>(new Map())
    const onOpenAttachment = useCallback(
        (message: UIConversationMessage, messages: UIConversationMessage[]) => {
            const activityId = activityIdsRef.current.get(message.id)
            if (activityId != null && onOpenActivity != null) {
                onOpenActivity(activityId)
                return
            }
            dispatchPreview({ type: "OPEN_EXISTING", message, messages })
        },
        [onOpenActivity]
    )

    const { state, actions, sending, attaching } = useStudentTutorConversationState({
        threadId,
        embedded: true,
        holdNewFile,
        onOpenAttachment,
    })
    const voice = useConversationVoicePlayer(threadId)

    const voiceQueue = useVoiceOutbox(messengerVoiceOutbox)
    const voiceEntries = useMemo(() => voiceOutboxEntries(voiceQueue.items, threadId), [voiceQueue.items, threadId])
    const outbox = useMemo(() => mergeOutboxes(state.outbox, voiceEntries), [state.outbox, voiceEntries])

    const sendVoice = useCallback(
        (file: VoiceFile) => {
            if (threadId == null) return
            messengerVoiceOutbox.enqueue(file, {
                threadId,
                fileName: messengerVoiceFileName(tutorName, file.extension),
            })
        },
        [threadId, tutorName]
    )
    const retryMessage = useCallback(
        (message: UIConversationMessage) => {
            if (voiceQueue.items.some((item) => item.id === message.id)) messengerVoiceOutbox.retry(message.id)
            else actions.retry(message)
        },
        [voiceQueue.items, actions]
    )
    const attach = useCallback(
        (source: Parameters<typeof actions.attach>[0]) => {
            if (source === "voice") voice.stop()
            actions.attach(source)
        },
        [actions, voice]
    )

    const viewModel = useMemo(makeTutorStudentConversationViewModel, [])
    const vm = useSelector((s: ReduxState) =>
        !on
            ? INACTIVE_TUTOR_CONVERSATION
            : viewModel(s, {
                  studentId,
                  threadId,
                  apiUrl: API_URL as string,
                  outbox,
                  error: data.error,
                  hasEarlier: data.hasEarlier,
                  voiceMessageId: voice.messageId,
                  voiceLabel: voice.label,
              })
    )
    activityIdsRef.current = vm.activityIdByMessage
    const canWrite = threadId != null

    const shown = preview.preview
    const closePreview = useCallback(() => dispatchPreview({ type: "CLOSE" }), [])
    const createActivity = useCallback(() => {
        if (shown == null || studentId == null) return
        const file = generationFileOf(shown.media)
        dispatchPreview({ type: "CLOSE" })
        openGeneration({ kind: "file", studentId, file })
    }, [shown, studentId, openGeneration])
    const sendToChat = useCallback(async () => {
        if (shown == null || !shown.fresh) return
        dispatchPreview({ type: "SENDING", sending: true })
        await actions.sendHeldFile(shown.media)
        dispatchPreview({ type: "CLOSE" })
    }, [shown, actions])

    if (student == null) return { props: undefined, overlay: null }

    const props: TutorConversationPageProps = {
        student,
        days: vm.days,
        loading: vm.loading,
        error: vm.error ?? undefined,
        onRetry: canWrite ? data.retry : undefined,
        hasEarlier: vm.hasEarlier,
        loadingEarlier: data.loadingEarlier,
        onLoadEarlier: data.loadEarlier,
        autoLoadEarlier: IS_WEB,
        onRetryMessage: retryMessage,
        onOpenAttachment: (message) => actions.openAttachment(message, vm.messages),
        onMessageLongPress: actions.openCopy,
        voicePlayback: voice.playback,
        onToggleVoice: voice.toggle,
        onSeekVoice: voice.seek,
        onCycleVoiceRate: voice.cycleRate,
        onPressLink: actions.pressLink,
        draft: state.draft,
        onChangeDraft: actions.changeDraft,
        onSend: actions.send,
        sending,
        attachSources: canWrite ? CONVERSATION_ATTACH_SOURCES : [],
        onAttach: attach,
        attaching,
        autoFocus: IS_WEB,
    }

    const position =
        shown != null && !shown.fresh && shown.media.kind === "image" && shown.images.length > 1
            ? { index: shown.index, count: shown.images.length }
            : null

    const overlay = on ? (
        <TutorStudentMessagesOverlay
            conversation={{
                copyOpen: state.copyMessage != null,
                onCopy: actions.copy,
                onCloseCopy: actions.closeCopy,
                viewer: state.viewer,
                onCloseViewer: actions.closeViewer,
                unsentNotice: state.unsentNotice,
                onHideUnsent: actions.hideUnsent,
                phone: state.phone,
                onChoosePhone: actions.choosePhone,
                onClosePhone: actions.closePhone,
                recorder:
                    state.recorderOpen && canWrite
                        ? { threadId, onClose: actions.closeRecorder, onSend: sendVoice }
                        : null,
            }}
            preview={
                shown == null
                    ? null
                    : {
                          onClose: closePreview,
                          media: { kind: shown.media.kind, uri: shown.media.uri, name: shown.media.name ?? undefined },
                          fresh: shown.fresh,
                          onCreateActivity: createActivity,
                          onSendToChat: shown.fresh ? () => void sendToChat() : undefined,
                          sending: preview.sending,
                          position,
                          onPrevious: () => dispatchPreview({ type: "NAVIGATE", index: shown.index - 1 }),
                          onNext: () => dispatchPreview({ type: "NAVIGATE", index: shown.index + 1 }),
                          onOpenViewer: () => dispatchPreview({ type: "OPEN_VIEWER" }),
                      }
            }
            viewer={
                shown != null && preview.viewerOpen && shown.media.kind === "image"
                    ? {
                          images: (shown.images.length > 0 ? shown.images : [shown.media]).map((item) => ({
                              uri: item.uri,
                          })),
                          index: shown.images.length > 0 ? shown.index : 0,
                          onClose: () => dispatchPreview({ type: "CLOSE_VIEWER" }),
                      }
                    : null
            }
        />
    ) : null

    return { props, overlay }
}
